import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TableTextWithDuplicateError } from '@/components/ui-v2/inputs/table/table-text-with-duplicate-error';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsCellEditing } from '@/hooks/use-is-cell-editing';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { cn } from '@/lib/utils';
import { createSubRowSortingFn } from '@/lib/utils/table-filter-utils';
import { useSetColumnSort } from '@/states/column-sort.state';
import { Row } from '@tanstack/react-table';
import { InfoIcon } from 'lucide-react';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

interface ColumnProps {
  handleCellClick: (id: string, field: keyof DealItem) => void;
  handleCellEdit: <K extends keyof DealItem>(
    id: string | undefined,
    field: K,
    value: DealItem[K],
    isLeafRow?: boolean
  ) => Promise<void>;
  handleKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  handleBlur: React.FocusEventHandler<HTMLInputElement>;
}

interface SkuCellContentProps {
  row: Row<DealItemWithVersions>;
  handleCellClick: ColumnProps['handleCellClick'];
  handleCellEdit: ColumnProps['handleCellEdit'];
  handleKeyDown: ColumnProps['handleKeyDown'];
  handleBlur: ColumnProps['handleBlur'];
}

function SkuCellContent({
  row,
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
  handleBlur,
}: SkuCellContentProps) {
  const isOfferLocked = useIsOfferLocked();
  const isEditing = useIsCellEditing(row, 'sku');
  const isParent = row.subRows.length > 1;

  const getValue = () => {
    const value = row.original.activeVersion.sku;
    if (typeof value === 'undefined' || value === null) return null;
    return value;
  };

  const getParentValue = () => {
    const subRowsValues: string[] = [];
    row.subRows.forEach((subRow) => {
      const value = subRow.original.activeVersion.sku;
      if (typeof value !== 'undefined' && value !== null) {
        subRowsValues.push(value);
      }
    });
    if (subRowsValues.length === 0) return null;
    const firstValue = subRowsValues[0];
    const allSame = subRowsValues.every((val) => val === firstValue);
    return allSame ? firstValue : null;
  };

  const displayValue = isParent ? getParentValue() : getValue();

  return (
    <TableCellWithRecentlyModified
      productId={row.original.id}
      field="sku"
      isParent={isParent}
    >
      {(isRecentlyModified) => (
        <div
          className={cn(
            'h-full w-full min-w-0 overflow-hidden',
            isParent || !isOfferLocked
              ? 'cursor-pointer'
              : 'cursor-not-allowed',
            (isParent || isOfferLocked) && 'opacity-50'
          )}
          onClick={() =>
            !isParent && handleCellClick(row.original.activeVersion.id, 'sku')
          }
        >
          <TableTextWithDuplicateError
            row={row}
            field="sku"
            value={row.original.activeVersion.sku ?? null}
            onChange={async (value) => {
              await handleCellEdit(
                row.original.activeVersion.id,
                'sku',
                value,
                !isParent
              );
            }}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={isOfferLocked}
            autoFocus={isEditing}
            displayValue={displayValue}
            isEditing={isEditing}
            isRecentlyModified={isRecentlyModified}
          />
        </div>
      )}
    </TableCellWithRecentlyModified>
  );
}

const useColumnDefSKU = ({
  handleBlur,
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const { setColumnSort } = useSetColumnSort();

  const accessorKey: keyof DealItem = 'sku';

  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'SKU',
      sortingFn: createSubRowSortingFn(
        'sku',
        (row) => row.activeVersion?.sku ?? null
      ),
      header: () => (
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <InfoIcon className="h-4 w-4" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('kpi.sku_tooltip')}</p>
            </TooltipContent>
          </Tooltip>{' '}
          <div
            className="flex flex-1 items-center gap-2"
            onClick={() => setColumnSort(accessorKey)}
          >
            {t('kpi.sku')}
            <SortIcon pointer="sku" />
          </div>
        </div>
      ),
      cell: ({ row }) => (
        <SkuCellContent
          row={row}
          handleCellClick={handleCellClick}
          handleCellEdit={handleCellEdit}
          handleKeyDown={handleKeyDown}
          handleBlur={handleBlur}
        />
      ),
      size: 200,
    }),
    [
      t,
      setColumnSort,
      handleBlur,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
    ]
  );
};

export default useColumnDefSKU;
